import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  APIError,
  FormFieldConfig,
  Quarter,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
  showTotalPagination,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import {
  Button,
  Col,
  FormInstance,
  Row,
  Spin,
  Table,
  message,
} from "antd";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { PlusOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { CREATE_QUARTER, GET_QUARTERS } from "../gql/economicIndicators";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { PageToolbar } from "../components/PageToolbar";

interface State {
  isLoading: boolean;
  quarters: Quarter[];
  page: number;
  pageSize: number;
  quartersCount: number;
  showNewQuarterModal: boolean;
  isCreatingQuarter: boolean;
  id: string | number;
}

export const NewQuarterUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isCreatingQuarter: boolean;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "name",
      name: "name",
      rule: "string",
      inputType: "string",
      required: true,
      label: "Quarter",
      placeholder: "Quarter",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "startDate",
      name: "startDate",
      rule: "object",
      inputType: "defaultDate",
      required: true,
      label: "Start Date",
      placeholder: "Start Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 22,
      },
    },
    {
      id: "endDate",
      name: "endDate",
      rule: "object",
      inputType: "defaultDate",
      required: true,
      label: "End Date",
      placeholder: "End Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 22,
      },
    },
    {
      id: "cediDepreciation",
      name: "cediDepreciation",
      rule: "number",
      inputType: "number",
      required: false,
      label: "Cedi Depreciation",
      placeholder: "Cedi Depreciation",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "gdp",
      name: "gdp",
      rule: "number",
      inputType: "number",
      required: false,
      label: "GDP currently available",
      placeholder: "GDP",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "inflation",
      name: "inflation",
      rule: "number",
      inputType: "number",
      required: false,
      label: "Inflation",
      placeholder: "Inflation",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "tbill91",
      name: "tbill91",
      rule: "number",
      inputType: "number",
      required: false,
      label: "T-Bill 91 Days",
      placeholder: "T-Bill 91",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "tbill182",
      name: "tbill182",
      rule: "number",
      inputType: "number",
      required: false,
      label: "T-Bill 182 Days",
      placeholder: "T-Bill 182",
      colConfig: {
        span: 22,
      },
    },
    {
      id: "tbill365",
      name: "tbill365",
      rule: "number",
      inputType: "number",
      required: false,
      label: "T-Bill 365 Days",
      placeholder: "T-Bill 365",
      colConfig: {
        span: 22,
      },
    },
  ];

  return (
    <Spin spinning={props.isCreatingQuarter}>
      <BaseForm
        layout="vertical"
        reference={props.reference}
        fieldsToHide={[]}
        fields={fields}
      />
    </Spin>
  );
};

export function getQuartersColumn<T extends Quarter>(
  quarters: Quarter[],
): ColumnType<T>[] {
  return [
    {
      title: "Display Name",
      dataIndex: "name",
      key: "name",
      render(text: string, record: Quarter): JSX.Element {

        return <span>{`${record?.displayName}`}</span>;
      },
    },
    {
      title: "Cedi Depreciation",
      dataIndex: "cediDepreciation",
      key: "cediDepreciation",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.cediDepreciation}%`}</span>;
      },
    },
    {
      title: "GDP",
      dataIndex: "gdp",
      key: "gdp",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.gdp}%`}</span>;
      },
    },
    {
      title: "Inflation",
      dataIndex: "inflation",
      key: "inflation",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.inflation}%`}</span>;
      },
    },
    {
      title: "T-Bill 91",
      dataIndex: "tbill91",
      key: "tbill91",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.tbill91}%`}</span>;
      },
    },
    {
      title: "T-Bill 182",
      dataIndex: "tbill182",
      key: "tbill182",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.tbill182}%`}</span>;
      },
    },
    {
      title: "T-Bill 365",
      dataIndex: "tbill365",
      key: "tbill365",
      render(text: number, record: Quarter): JSX.Element {
        return <span>{`${record?.tbill365}%`}</span>;
      },
    },
  ];
}

export default class Quarters extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      quarters: [],
      page: 1,
      pageSize: 25,
      quartersCount: 0,
      isCreatingQuarter: false,
      showNewQuarterModal: false,
      id: "",
    };
    this.reference = React.createRef<FormInstance>();
    this.nextPage = this.nextPage.bind(this);
    this.createQuarter = this.createQuarter.bind(this);
    this.redirectToNewQuarterPage = this.redirectToNewQuarterPage.bind(this);
    this.toggleCreateQuarterModal = this.toggleCreateQuarterModal.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.getQuarters();
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState({ page, pageSize }, async () => {
      await this.getQuarters();
    });
  }

  getQuarters = async (): Promise<void> => {
    const { page } = this.state;
    this.setState({ isLoading: true });
    try {
      const response: any = await request({
        query: GET_QUARTERS,
        variables: { page },
      });
      const { data } = response;
      this.setState({
        quarters: data.data?.quarters?.results || [],
        quartersCount: data.data?.quarters?.count || 0,
      });
    } catch (error) {
      message.error(`Failed To Load Quarters - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleCreateQuarterModal(): void {
    this.setState((prevState) => ({
      showNewQuarterModal: !prevState.showNewQuarterModal,
    }));
  }

  redirectToNewQuarterPage = (): void => {
    this.props.history.push("/quarters/new");
  };

  async createQuarter(): Promise<void> {
    interface Response {
      quarter: Quarter;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "name",
        "startDate",
        "endDate",
        "cediDepreciation",
        "gdp",
        "inflation",
        "tbill91",
        "tbill182",
        "tbill365",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_QUARTER,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createQuarter",
      );

      this.setState({ isCreatingQuarter: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.quarter?.id }, () => {
          message.success("Quarter created sucessfully");
          this.redirectToViewQuarterPage();
        });
      }
    } catch (error) {
      message.error(`Failed to create quarter - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isCreatingQuarter: false });
    }
  }

  redirectToViewQuarterPage(): void {
    this.props.history.push(`/quarters/${this.state.id}`);
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      quarters,
      page,
      pageSize,
      quartersCount,
      isCreatingQuarter,
      showNewQuarterModal,
    } = this.state;

    const resultsWithKeys: Quarter[] = quarters.map(
      (result: Quarter): Quarter => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
      <Card>
          <div className="asset-allocation-rates-container">
            <PageToolbar
              totalCount={quartersCount}
              currentDataCount={quarters.length}
              pageSize={pageSize}
              modelName="quarters"
              primaryAction={
                <EntitledSection
                  entitlement={entitlements.EconomicIndicators.create}
                >
                  <Button
                    className="base-button"
                    size="large"
                    data-testid="create-quarter-button"
                    onClick={this.toggleCreateQuarterModal}
                  >
                    <PlusOutlined />
                    Create Quarter
                  </Button>
                </EntitledSection>
              }
            />

            <Row>
              <Col span={24}>
                <Table
                  loading={isLoading}
                  scroll={{ x: 400 }}
                  columns={getQuartersColumn(quarters)}
                  dataSource={resultsWithKeys}
                  onRow={(record) => ({
                    onClick: () => {
                      this.props.history.push({
                        pathname: `/quarters/${record.id}`,
                        state: { quarters },
                      });
                    },
                    style: { cursor: 'pointer' }

                  })}
                  pagination={{
                    current: page,
                    defaultPageSize: 25,
                    total: quartersCount,
                    onChange: this.nextPage,
                    showSizeChanger: false,
                    showTotal: showTotalPagination,
                  }}
                />
              </Col>
            </Row>
            {showNewQuarterModal &&
              ModalCreator({
                title: "Create Quarter",
                okType: "primary",
                okText: "Create",
                showModal: showNewQuarterModal,
                onOkay: this.createQuarter,
                toggleModal: this.toggleCreateQuarterModal,
                inscription: NewQuarterUI({
                  reference: this.reference,
                  isCreatingQuarter,
                }),
              })}
          </div>
        </Card>
    );
  }
}
